import { memo } from "react";
import { useHistory } from "react-router";
import {
  Box,
  Button,
  Grid,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";

import FormikFormField from "components/shared/FormikFormField";
import { Hero2Text } from "components/shared/Typography";
import { FORGET_PASSWORD } from "helpers/apis";
import useAxios from "helpers/axios";
import { ToastTypes } from "hooks/useApp";
import useDialog from "hooks/useDialog";
import { IForm, errors } from "types/form";

interface IForgotPasswordForm extends IForm {
  email: string;
}

const validationSchema = yup.object({
  email: yup.string().email(errors.invalidEmail).required(errors.required),
});

const ForgotPasswordForm = () => {
  const history = useHistory();
  const {
    openAccountRecoveryDialog,
    setOpenPasswordRecoveryDialog,
    setOpenAccountRecoveryDialog,
  } = useDialog();
  const inNarrowMode =
    useMediaQuery((theme: Theme) => theme.breakpoints.down('sm')) ||
    openAccountRecoveryDialog;

  const { fetchData, loading } = useAxios({
    config: { url: FORGET_PASSWORD, method: "POST" },
    callOnInit: false,
  });

  const onSubmit = (values: IForgotPasswordForm) => {
    fetchData(
      { data: values },
      ({
        response,
        setToastMessage,
      }: {
        response: {
          data: null;
          message: string;
        };
        setToastMessage: Function;
      }) => {
        if (openAccountRecoveryDialog) {
          setOpenAccountRecoveryDialog(false);
          setOpenPasswordRecoveryDialog(true);
        } else {
          history.push("/password-recovery");
        }
        setToastMessage({
          message: response.message,
          type: ToastTypes.SUCCESS,
        });
      }
    );
  };

  return (
    <Formik
      initialValues={{ email: "" }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<IForgotPasswordForm>) => {
        const { dirty, isValid } = props;

        return (
          <Form>
            <Box mb={3}>
              <Hero2Text align={inNarrowMode ? "center" : "inherit"}>
                Forgot your password?
              </Hero2Text>
            </Box>
            <Grid
              container
              spacing={3}
              justifyContent={inNarrowMode ? "center" : "flex-start"}
            >
              <Grid item>
                <Typography
                  variant="body1"
                  align={inNarrowMode ? "center" : "inherit"}
                >
                  Forgot your password? Please enter the email address you'd
                  like your password reset information sent to.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormikFormField<IForgotPasswordForm>
                  name="email"
                  label="Email Address"
                  type="email"
                  {...props}
                />
              </Grid>
              <Grid item>
                <Box mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading || !dirty || !isValid}
                    type="submit"
                    size="large"
                    fullWidth={inNarrowMode}
                  >
                    Send password reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default memo(ForgotPasswordForm);
